<template>
  <div :class="['gu-datagrid-new gu-datagrid-client' , props.class]">
    <grid-menu
      v-if="gridReady && !isSiteConfigLoading"
      :config="gridMenuConfig"
      :grid-api="gridApi"
      :admin="isAdmin"
      :user-role="userRole"
      :initial-column-state="initialColumnState"
    />
  
    <applied-filters
      v-if="gridReady && !isSiteConfigLoading && showActiveFilters"
      :grid-api="gridApi"
    />
  
    <ag-grid-vue
      v-if="columnDefs.length"
      :grid-options="internalGridOptions"
      :row-data="rowData"
      :class="['ag-grid-vue gu-datagrid-theme ag-theme-quartz h-100', props.class]"
      @grid-ready="handleGridReady"
      @pagination-changed="handlePaginationChanged"
      @selection-changed="handleSelectionChanged"
      @first-data-rendered="handleFirstDataRendered"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'

import { httpRequest } from '@shared/api/httpRequests'

import AppliedFilters from '@gu-datagrid-new/components/AppliedFilters.vue'
import GridMenu from '@gu-datagrid-new/components/grid-menu/GridMenu.vue'

import { useGridConfig } from '@gu-datagrid-new/composables/GridConfig'
import { useGridOptions } from '@gu-datagrid-new/composables/GridOptions'
import { useGridState } from '@gu-datagrid-new/composables/GridState'
import { useAgGridLicense } from '@gu-datagrid-new/composables/LicenseManager'
import { usePagination } from '@gu-datagrid-new/composables/Pagination'
import { useSelection } from '@gu-datagrid-new/composables/Selection'
import { useSiteConfigLoader } from '@gu-datagrid-new/composables/SiteConfigLoader'
import { useRowModelType } from '@gu-datagrid-new/composables/RowModelType'

const props = defineProps({
  config: {
    type: Object,
    default: () => ({
      rowData: [],
      gridOptions: {
        columnDefs: [],
      },
    }),
    description: 'Datagrid configuration (both GU props and Ag-Grid options)',
  },
  siteConfigName: {
    type: String,
    default: null,
    description: 'Site config name that contains the grid configuration. If set, the grid will fetch this content override from API',
  },
  class: {
    type: String,
    default: '',
  },
  applyGridStateFromStorage: {
    type: Boolean,
    default: false,
    description: 'Whether to save and restore grid state (filters, sorting, pagination) from sessionStorage',
  },
})

const emits = defineEmits(['fail', 'request-fired', 'rows-selected', 'success', 'grid-ready'])

useAgGridLicense()

const { 
  siteConfig,
  isLoading: isSiteConfigLoading,
  error: siteConfigError,
} = useSiteConfigLoader(props.siteConfigName)

const {
  gridMenuConfig,
  pinnedColumns,
  showActiveFilters,
} = useGridConfig(props, siteConfig)

const { 
  columnDefs,
  gridOptions: baseGridOptions, 
} = useGridOptions(siteConfig, props.config)

const { onPaginationChanged, markInitialRequestComplete } = usePagination()
const { onSelectionChanged } = useSelection(emits)

watch(() => siteConfigError.value, (newError) => {
  if (newError) {
    console.error('GuDatagridClient siteConfig error:', newError)
  }
})

const gridReady = ref(false)
const gridApi = ref(null)
// eslint-disable-next-line no-unused-vars
const { rowModelType } = useRowModelType('clientSide')

const gridState = useGridState(props.applyGridStateFromStorage)

const isAdmin = ref(false)
const userRole = ref('Role1')

const rowData = ref([])

onMounted(async () => {
  const url = siteConfig?.value?.apiSettings?.rowsUrl || props.config?.rowsUrl
  if (url) {
    try {
      const response = await httpRequest({
        method: 'POST',
        url,
        data: {},
      })

      rowData.value = response.rowData || []
    } catch (error) {
      emits('fail', error)
      console.error('Failed to load row data:', error)
    }
  } else {
    rowData.value = props.config?.rowData || []
  }
})

const initialColumnState = computed(() => {
  const columns = columnDefs.value.map((colDef, index) => ({
    colId: colDef.field || `col_${index}`,
    width: colDef.width || undefined,
    visible: colDef.hide !== true,
    pinned: pinnedColumns.value.includes(colDef.field) ? 'left' : undefined,
  }))

  if (gridMenuConfig.value.enableMultirowSelect) {
    columns.unshift({
      colId: 'selection',
      width: 48,
      visible: true,
      pinned: 'left',
    })
  }

  return columns
})

const internalGridOptions = computed(() => ({
  paginationPageSize: 20,
  ...baseGridOptions.value,
  columnDefs: columnDefs.value,
  rowModelType: 'clientSide',
  getRowId: (params) => {
    if (baseGridOptions?.value?.defaultColumnId) return String(params.data[baseGridOptions?.value?.defaultColumnId])
    return String(params.data.id)
  },
  onFilterChanged: () => {
    if (gridReady.value) {
      gridState.saveCurrentGridState(gridApi.value, null)
    }
  },
  onSortChanged: () => {
    if (gridReady.value) {
      gridState.saveCurrentGridState(gridApi.value, null)
    }
  },
}))

const handleGridReady = (params) => {
  gridApi.value = params.api
  gridReady.value = true
  gridState.setURLorStorageFilterModel(params.api)

  gridState.syncFilterModel(params.api)
  emits('grid-ready', params)
}

const handlePaginationChanged = (event) => {
  try {
    onPaginationChanged(event, gridApi, gridState)
  } catch (error) {
    console.error('Error in handlePaginationChanged:', error)
  }
}

const handleFirstDataRendered = () => {
  setTimeout(() => {
    markInitialRequestComplete(gridApi, gridState)
  }, 0)
}

const handleSelectionChanged = () => {
  try {
    onSelectionChanged(gridApi)
  } catch (error) {
    console.error('Error in handleSelectionChanged:', error)
  }
}
</script>
